import { FUNNEL, type Screen } from "../routes/registry";
import {
  FIRST_INSTANCE,
  instanceKey,
  instancePath,
  type Entry,
} from "../funnel/order";
import type { SitePage } from "./registry";

/**
 * ═══ UNE ÉTAPE SERVIE COMME PAGE DU SITE ═══
 *
 * « Mes informations » dans le menu, « Ma société » en tuile sur l'accueil : le
 * participant revient sur ses réponses sans retraverser l'inscription.
 *
 * ⚠️ C'EST LE MÊME ÉCRAN, PAS UNE COPIE. Même formulaire, mêmes réglages, même nom —
 * une seconde définition du même écran aurait divergé de la première au premier
 * changement, et l'organisateur aurait réglé deux fois la même chose.
 *
 * ⚠️ ET C'EST UNE `SitePage`, pas un troisième genre de page. Le menu, les tuiles, la
 * barre du bas, le gestionnaire et le sélecteur d'écran lisent tous cette forme-là :
 * un genre de plus, et il aurait fallu l'apprendre à chacun d'eux — cinq occasions
 * d'en oublier un. Deux champs facultatifs suffisent à dire la différence, l'adresse
 * et le nom (`path`, `nameKey`), parce que c'est l'ÉTAPE qui les porte.
 *
 * ⚠️ INDÉPENDANT DE L'INTERRUPTEUR DU PARCOURS, ET C'EST VOULU. Une étape peut être
 * HORS du tunnel et dans le menu : un événement qui n'interroge personne à
 * l'inscription mais laisse chacun compléter son profil ensuite est un cas réel. Les
 * deux réglages sont donc ceux d'une page — « dans le menu », « en tuile » — et rien
 * d'autre ne conditionne sa présence.
 *
 * Les textes affichés sont français : c'est un produit français.
 */
export type StepPage = SitePage & {
  /** L'écran servi — c'est lui qui porte le formulaire et les réglages. */
  step: Screen & {
    funnel: string;
    stepName: string;
    standalone: { name: string; icon: string; subtitle: string };
  };
  /** L'instance servie, `undefined` pour la première. */
  instance?: string;
};

/** Les étapes que le produit autorise à sortir du tunnel. */
export const STANDALONE_STEPS = FUNNEL.filter(
  (s): s is StepPage["step"] => !!s.funnel && !!s.standalone,
);

/**
 * ⚠️ L'ADRESSE EST AU PREMIER NIVEAU, comme celle des pages du site — décision prise
 * le 2026-08-03 en retirant `/p`. Elle partage donc l'espace de noms des alias
 * d'événement : vérifié en base au moment de l'écrire, aucun des événements existants
 * ne s'appelle « informations », « company » ni « companions ». Revérifié le 2026-08-09
 * en ajoutant les quatre autres : sur 725 événements, dont 292 portent un alias, aucun
 * ne s'appelle « participation », « typology », « dates » ni « articles ». Une route
 * statique bat `/:context`, donc un alias homonyme rendrait son événement inatteignable.
 * Le test du registre interdit par ailleurs qu'une page prenne l'adresse d'un écran déjà
 * déclaré.
 *
 * ⚠️ ET LA CLÉ NE FAIT PAS TOUJOURS L'ADRESSE : l'étape de participation s'appelle
 * `registration`, qui est déjà l'adresse de la première étape du tunnel. Elle déclare
 * donc la sienne (`slug`), et c'est la seule chose que ce chemin lit de travers si on
 * l'oublie : la route se serait montée sous une adresse que le routeur donne au tunnel,
 * en silence. Voir `standalone` dans le registre.
 */
export const stepPageSlug = (step: StepPage["step"]) =>
  step.standalone.slug ?? step.key;

export const stepPagePath = (step: StepPage["step"], instance?: string) =>
  instancePath(`/${stepPageSlug(step)}`, instance);

/**
 * La clé de page d'une instance : `informations`, `informations_2`.
 *
 * ⚠️ ELLE SERT À FABRIQUER `FO_MEETING_V2_NAV_*` ET `_TILE_*`, donc la première
 * instance ne se suffixe pas — même règle que partout ailleurs, pour la même raison :
 * ce qui a déjà été réglé doit rester lu.
 */
export const stepPageKey = (step: StepPage["step"], instance?: string) =>
  instanceKey(step.key, instance);

/** Une instance d'étape, sous la forme que lisent le menu et les tuiles. */
function pageDe(step: StepPage["step"], instance?: string): StepPage {
  return {
    key: stepPageKey(step, instance),
    /* ⚠️ LE NOM DE LA PAGE, PAS CELUI DE L'ÉTAPE, et seulement en dernier recours :
       `nameKey` passe devant. Renommée dans le parcours, la page suit — c'est une seule
       chose, elle n'a qu'un mot. Non renommée, elle s'appelle « Mes informations » :
       le possessif dit à QUI sont ces informations, et il distinguait cette page de
       la page libre du site, qui s'appelait « Informations » et s'appelle aujourd'hui
       « Section ». Le mot reste le bon même sans la collision. */
    name: step.standalone.name,
    icon: step.standalone.icon,
    subtitle: step.standalone.subtitle,
    path: stepPagePath(step, instance),
    nameKey: instanceKey(step.funnel, instance),
    step,
    ...(instance && instance !== FIRST_INSTANCE ? { instance } : {}),
  };
}

/**
 * Les pages que les étapes peuvent servir, dans l'ordre du parcours.
 *
 * ⚠️ LE PARCOURS EST NÉCESSAIRE, ET PAS SEULEMENT POUR L'ORDRE : lui seul sait combien
 * d'instances d'une étape répétable existent. Sans lui, une seconde étape
 * « informations » n'aurait jamais eu de page — le même trou que le sélecteur d'écran
 * a déjà eu.
 */
export function stepPages(
  entries: ReadonlyArray<Entry>,
): ReadonlyArray<StepPage> {
  return entries.flatMap((entry) => {
    const step = STANDALONE_STEPS.find((s) => s.key === entry.key);
    return step ? [pageDe(step, entry.instance)] : [];
  });
}

/**
 * L'étape que sert une adresse de page, s'il y en a une.
 *
 * ⚠️ PURE VIS-À-VIS DE LA CONFIGURATION, et il le faut : les routes se construisent au
 * chargement du module, la configuration arrive après. `/informations/2` se lit sur sa
 * seule forme, exactement comme la route `…/informations/:instance` du tunnel.
 */
export function stepPageOf(pathname: string): StepPage | undefined {
  for (const step of STANDALONE_STEPS) {
    const base = `/${stepPageSlug(step)}`;
    if (pathname === base) return pageDe(step);
    if (step.repeatable && pathname.startsWith(`${base}/`)) {
      const instance = pathname.slice(base.length + 1);
      if (instance && !instance.includes("/")) return pageDe(step, instance);
    }
  }
  return undefined;
}
